<template>
  <div class="card">
    <div class="card__top">
      <p class="card__top-name">{{ userName }}</p>
      <p class="card__top-mail">{{ userEmail }}</p>
    </div>
    <div class="card__topup">
      <p class="card__topup-title">{{ t('account.balance.title') }}: <span>€ {{ balance }}</span></p>
      <div class="card__topup-inputs">
        <ui-input
          :type="'text'"
          :placeholder="t('fields.fiat')"
          :rules="[required]"
          v-model="amount"
        />
        <ui-input
          :type="'text'"
          :placeholder="'Easies'"
          :rules="[required]"
          v-model="amount"
        />
      </div>
      <button class="card__topup-button" @click="topUp">{{ t('buttons.topUp') }}</button>
    </div>
    <div class="card__buttons">
      <router-link :to="translations.i18nRoute({ name: 'profile', query: { edit: 'true' } })" class="card__buttons-button">{{ t('buttons.edit') }}</router-link>
      <button class="card__buttons-button" @click="scrollTo('contact', 'home')">{{ t('buttons.contact') }}</button>
      <router-link :to="translations.i18nRoute({ name: 'faq' })" class="card__buttons-button">{{ t('buttons.support') }}</router-link>
      <button class="card__buttons-button" @click="logout">{{ t('buttons.logout') }}</button>
    </div>
  </div>
</template>

<script setup>
import {useRouter} from "vue-router";
import {scroll} from "@/core/helpers/scrollTo.js";
import {computed, ref} from "vue";
import {useAuthStore} from "@/stores/auth.js";
import {useI18n} from "vue-i18n";
import UiInput from "@/components/ui/ui-input.vue";
import {required} from "@/core/rules/textFieldRules.js";
import translations from "@/core/helpers/translations.js";
import {Processes} from "@/core/api/processes.js";
import {ElNotification} from "element-plus";

const {t} = useI18n()
const router = useRouter()
const authStore = useAuthStore()

const userName = computed(() => {
  return authStore.user?.firstName ? authStore.user.firstName + ' ' + authStore.user.lastName : ''
})
const userEmail = computed(() => {
  return authStore.user?.email ? authStore.user.email : ''
})
const accessToken = computed(() => {
  return authStore.accessToken ? authStore.accessToken : ''
})
const balance = computed(() => {
  return authStore.user?.balance ? authStore.user.balance.amount : 0
})

const amount = ref(5)

const scrollTo = (id, routeName) => {
  router.push({
    name: routeName
  }).then(scroll(id, 400));
}

const logout = async () => {
  authStore.setUser({
    user: null,
    accessToken: null
  })

  localStorage.removeItem('gce-refresh')

  await router.push({
    name: 'home',
    params: {
      locale: localStorage.getItem('gce-user-locale') || 'en-gb'
    }
  })
}

const topUp = async () => {
  const res = await Processes.topUpBalance(amount.value, accessToken.value)

  if (res.data.deposit.transaction.process) {
    window.location.href = res.data.deposit.transaction.process.url
  } else {
    ElNotification({
      message: res.errors[0].message,
      type: 'error'
    })
  }
}
</script>

<style lang="scss" scoped>
.card {
  width: 450px;
  margin-block: 85px;
  border-radius: 40px;
  background-color: $white;
  padding: 45px 35px;
  display: flex;
  flex-direction: column;
  gap: 15px;
  flex-shrink: 0;

  &__top {
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    &-name {
      color: $black;
      font-family: Poppins, sans-serif;
      font-size: 20px;
      font-weight: 600;
    }

    &-mail {
      color: #4a4a4a;
      font-family: Nunito, sans-serif;
      font-size: 20px;
      font-weight: 600;
    }
  }

  &__topup {
    border-top: 1px solid $black;
    border-bottom: 1px solid $black;
    padding-block: 15px;
    display: flex;
    flex-direction: column;
    gap: 20px;

    &-title {
      width: fit-content;
      padding-inline: 5px;
      border-bottom: 2px solid #d9dee4;
      color: $black;
      font-family: Poppins, sans-serif;
      font-size: 20px;
      font-weight: 600;

      & span {
        margin-left: 5px;
        font-family: Nunito, sans-serif;
      }
    }

    &-inputs {
      display: flex;
      align-items: flex-start;
      gap: 20px;
    }

    &-button {
      cursor: pointer;
      width: fit-content;
      border: 1px solid $black;
      border-radius: 4px;
      background-color: transparent;
      padding: 2px 20px;
      transition: 0.2s;

      color: #2a2a2a;
      font-family: Poppins, sans-serif;
      font-size: 16px;
      font-weight: 600;

      @include hover {
        background-color: $main;
        color: $white;
      }

      &:disabled {
        cursor: not-allowed;
        background-color: transparent;
      }

      &:disabled:hover {
        color: #2a2a2a;
      }
    }
  }

  &__buttons {
    display: flex;
    flex-direction: column;
    gap: 25px;

    &-button {
      cursor: pointer;
      width: 100%;
      border-radius: 8px;
      background-color: rgba(47, 107, 79, 0.4);
      padding-block: 3px;
      display: grid;
      place-items: center;
      transition: 0.2s;

      color: $white;
      font-family: Poppins, sans-serif;
      font-size: 20px;
      font-weight: 600;

      @include hover {
        background-color: rgba(47, 107, 79, 0.7);
      }
    }
  }
}

@media (max-width: 1300px) {
  .card {
    margin-block: 40px;
  }
}

@media (max-width: 500px) {
  .card {
    width: 100%;
  }
}
</style>